import { Link } from "react-router-dom";
import { ArrowRight, ShieldCheck, Activity } from "lucide-react";
import { Button } from "@/components/ui/button";
import { useQuote } from "@/components/QuoteDialog";
import { stats, images, company } from "@/mock/mock";

export default function Hero() {
  const { openQuote } = useQuote();
  return (
    <section className="relative overflow-hidden pt-40 pb-24 lg:pt-44 lg:pb-28">
      {/* background layers */}
      <div className="absolute inset-0 bg-brand-navy" />
      <div className="absolute inset-0 grid-overlay opacity-60" />
      <div className="absolute -top-40 -right-40 h-[520px] w-[520px] rounded-full bg-brand-lime/10 blur-[120px]" />
      <div className="absolute top-20 -left-40 h-[420px] w-[420px] rounded-full bg-brand-cyan/10 blur-[120px]" />

      <div className="relative mx-auto max-w-7xl px-6">
        <div className="grid lg:grid-cols-2 gap-14 items-center">
          <div>
            <div className="inline-flex items-center gap-2 rounded-full border border-brand-lime/30 bg-brand-lime/5 px-4 py-1.5 text-xs font-medium text-brand-lime">
              <span className="relative flex h-2 w-2">
                <span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-brand-lime opacity-75" />
                <span className="relative inline-flex h-2 w-2 rounded-full bg-brand-lime" />
              </span>
              24/7 SOC · Now operating in Halifax, Canada
            </div>

            <h1 className="mt-6 font-display text-4xl sm:text-5xl lg:text-[3.4rem] font-bold leading-[1.08] text-white">
              Cyber security is the <span className="text-gradient">business unit</span> that is not optional
            </h1>

            <p className="mt-6 max-w-xl text-lg leading-relaxed text-slate-400">
              {company.legal} is a Managed Security Service Provider (MSSP) delivering 24/7 SOC monitoring, digital forensics and incident response — so you can grow with confidence.
            </p>

            <div className="mt-9 flex flex-wrap items-center gap-4">
              <Button onClick={openQuote} className="group bg-brand-orange hover:bg-brand-orangeDark text-white font-semibold rounded-full px-7 h-12 text-base">
                Get Started
                <ArrowRight className="ml-2 h-5 w-5 transition-transform group-hover:translate-x-1" />
              </Button>
              <Button asChild variant="outline" className="rounded-full px-7 h-12 text-base border-white/20 bg-white/5 text-white hover:bg-white/10 hover:text-white">
                <Link to="/services">Explore Services</Link>
              </Button>
            </div>

            <div className="mt-10 flex items-center gap-6 text-sm text-slate-400">
              <span className="flex items-center gap-2"><ShieldCheck className="h-4 w-4 text-brand-lime" /> PIPEDA aligned</span>
              <span className="flex items-center gap-2"><Activity className="h-4 w-4 text-brand-lime" /> Canadian data residency</span>
            </div>
          </div>

          {/* visual */}
          <div className="relative">
            <div className="relative rounded-2xl overflow-hidden border border-white/10 glow-lime">
              <img src={images.socRoom} alt="Security Operations Center" className="w-full h-[420px] object-cover" />
              <div className="absolute inset-0 bg-gradient-to-t from-brand-navy via-brand-navy/30 to-transparent" />
              <div className="absolute bottom-4 left-4 right-4 glass rounded-xl p-4 flex items-center justify-between">
                <div>
                  <p className="text-xs text-slate-400">Live threat status</p>
                  <p className="font-display text-lg font-semibold text-white">All systems monitored</p>
                </div>
                <span className="rounded-full bg-brand-lime/15 px-3 py-1 text-xs font-semibold text-brand-lime">SECURE</span>
              </div>
            </div>
            <div className="absolute -bottom-6 -left-6 hidden sm:block glass rounded-xl p-4 animate-float">
              <p className="font-display text-2xl font-bold text-white">&lt;15m</p>
              <p className="text-xs text-slate-400">Avg. response time</p>
            </div>
          </div>
        </div>

        {/* stats */}
        <div className="mt-20 grid grid-cols-2 lg:grid-cols-4 gap-px rounded-2xl overflow-hidden border border-white/10 bg-white/5">
          {stats.map((s) => (
            <div key={s.label} className="bg-brand-navy2/60 px-6 py-8 text-center">
              <p className="font-display text-3xl lg:text-4xl font-bold text-brand-lime">{s.value}</p>
              <p className="mt-2 text-sm text-slate-400">{s.label}</p>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}
